import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { BreezeChannelIdentifier } from '@microsoft/applicationinsights-common';
import { ClickAnalyticsPlugin } from '@microsoft/applicationinsights-clickanalytics-js';
import { CognitoConfiguration } from '@cognitoforms/utils/clientside-configuration';
import { globalStore } from 'src/stores/global-store';
import { installJourneyTelemetry } from 'src/utilities/journey';
import { isPrerender } from '@cognitoforms/utils/utilities';
import { serviceRequest } from '@cognitoforms/api/services/service-request';
import session from '@cognitoforms/api/services/session';
import Vue from 'vue';
import type { IApplicationInsights } from '@microsoft/applicationinsights-web';

let appInsights: ApplicationInsights = null;
let clickAnalytics: ClickAnalyticsPlugin = null;

function relayFailedTelemetry(payload: string[]) {
	const items = payload.map(item => {
		try {
			const envelope = JSON.parse(item);
			envelope.iKey = 'faux-key';
			return envelope;
		}
		catch (e) {
			return null;
		}
	}).filter(Boolean);

	if (items.length)
		serviceRequest('svc/log', items, { method: 'POST' }).catch(() => { });
}

function hookSenderFailures(ai: ApplicationInsights) {
	const senderPlugin = ai.core.getPlugin(BreezeChannelIdentifier)?.plugin as any;
	if (!senderPlugin || typeof senderPlugin._onError !== 'function')
		return;

	const originalOnError = senderPlugin._onError.bind(senderPlugin);
	senderPlugin._onError = (payload: string[], message: string, event?: Event) => {
		originalOnError(payload, message, event);
		relayFailedTelemetry(payload);
	};
}

function installSessionTelemetry(ai: ApplicationInsights) {
	ai.addTelemetryInitializer(function (envelope) {
		const info = session.value;
		if (!info?.isAuthenticated)
			return;

		const tags = envelope.tags;
		tags['ai.user.id'] = info.userId;
		tags['ai.session.id'] = info.telemetrySessionId;

		const telemetryItem = envelope.baseData;
		telemetryItem.properties = telemetryItem.properties || {};
		telemetryItem.properties['OrganizationId'] = info.organizationId;
		telemetryItem.properties['Source'] = 'client';

		const currentFormId = globalStore.currentForm?.Id;
		if (currentFormId && !telemetryItem.properties['FormId'])
			telemetryItem.properties['FormId'] = currentFormId;
	});

	ai.addTelemetryInitializer(function (envelope) {
		if (session.value?.isAuthenticated && envelope.baseType === 'RemoteDependencyData') {
			const data = envelope.baseData;
			if (data && data.responseCode.toString().startsWith('2') && data.duration < 10000)
				return false;
		}
		return true;
	});
}

if (!isPrerender() && process.env.NODE_ENV !== 'development' && process.env.NODE_ENV !== 'test') {
	try {
		clickAnalytics = new ClickAnalyticsPlugin();
		appInsights = new ApplicationInsights({
			config: {
				connectionString: CognitoConfiguration.AppInsightsConnectionString,
				excludeRequestFromAutoTrackingPatterns: [/\/svc\/log(\?|$)/],
				extensions: [clickAnalytics],
				extensionConfig: {
					[clickAnalytics.identifier]: {
						autoCapture: true,
						dropInvalidEvents: true,
						dataTags: {
							customDataPrefix: 'data-cf-',
							useDefaultContentNameOrId: false
						}
					}
				}
			}
		});
		appInsights.loadAppInsights();
		hookSenderFailures(appInsights);
		installSessionTelemetry(appInsights);
		installJourneyTelemetry(appInsights, () => clickAnalytics);
	}
	catch (e) {
		appInsights = null;
		clickAnalytics = null;
		console.warn('AI failed to initialize:', e);
	}
}

export const AppInsights: () => IApplicationInsights = () => appInsights;

export const ClickAnalytics: () => ClickAnalyticsPlugin = () => clickAnalytics;

export default Vue.extend({
	name: 'CAppInsights',
	data() {
		return {
			appInsights
		};
	},
	watch: {
		$route: {
			immediate: true,
			handler(route) {
				if (appInsights && !route.meta.customReadyEvent)
					appInsights.stopTrackPage(route.name ?? route.path);
			}
		}
	},
	render() {
		return this.$slots.default;
	}
});